import { forwardRef, useMemo, useState, type FormEventHandler } from 'react'
import { useTranslation } from 'react-i18next'
import type { ImeiHubServiceId } from '../ImeiHubSignalIcon'
import ImeiHubServiceTitle from '../ImeiHubServiceTitle'
import VerifyStatusBadge from './VerifyStatusBadge'
import {
  IconBookmark,
  IconClose,
  IconLock,
  IconRefresh,
} from './VerifyReportIcons'
import ImeiHubServicePhosphorIcon from '../imeiHubServicePhosphorIcon'
import { formatImeiHubPriceEur } from '@redevicer/shared'
import type { VerifyCoverageStats, VerifyReportModel } from '../../utils/verifyReportAdapter'
import { hubServiceForCheckIcon } from '../../utils/verifyCheckFollowUp'
import { imeiHubHeroImage } from '../../utils/imeiHubHeroImage'

type Props = {
  report: VerifyReportModel
  service: ImeiHubServiceId
  serviceTitle: string
  variant: 'inline' | 'full'
  onClose?: () => void
  onDownloadPdf?: () => void
  pdfBusy?: boolean
  pdfError?: string
  onNewCheck?: () => void
  onRequireLogin?: () => void
  showDownloadPdf: boolean
  guestLocked: boolean
  preview?: boolean
  /** Same component tree as web report, captured for PDF (html2canvas). */
  exportMode?: boolean
  onFollowUpCheck?: (service: ImeiHubServiceId, checkId: string) => void
  followUpBusy?: boolean
  followUpError?: string
  followUpImeiPrompt?: {
    masked: string
    serviceTitle: string
    value: string
    error?: string
    busy?: boolean
    onChange: (value: string) => void
    onSubmit: FormEventHandler
    onCancel: () => void
  } | null
  followUpPriceCents?: Partial<Record<ImeiHubServiceId, number>>
}

const VERDICT_LABEL: Record<string, string> = {
  clean: 'vc.imeiReport.status.clean',
  attention: 'vc.imeiReport.status.warning',
  risk: 'vc.imeiReport.status.highRisk',
  unknown: 'vc.imeiReport.chip.unknown',
  invalid: 'vc.imeiReport.status.invalid',
  balance: 'vc.imeiReport.status.balance',
}

function VerifyResultBadge({
  stats,
  verdict,
  headlineKey,
}: {
  stats: VerifyCoverageStats
  verdict: string
  headlineKey: string
}) {
  const { t } = useTranslation()
  const noIssuesHeadline = headlineKey === 'vc.imeiReport.result.noIssuesFound'
  const showBang = !noIssuesHeadline && (verdict === 'risk' || verdict === 'attention')
  const toneClass =
    verdict === 'risk'
      ? 'vr-result--risk'
      : !noIssuesHeadline && verdict === 'attention'
        ? 'vr-result--warn'
        : 'vr-result--clean'

  return (
    <div className={`vr-result ${toneClass}`}>
      <div className="vr-result__mark" aria-hidden="true">
        {showBang ? (
          <span className="vr-result__mark-char">!</span>
        ) : (
          <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="M20 6 9 17l-5-5"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </div>
      <div className="vr-result__copy">
        <p className="vr-result__headline">{t(headlineKey)}</p>
        <p className="vr-result__coverage-line">
          {[
            stats.verified > 0
              ? `${stats.verified} ${t('vc.imeiReport.coverage.verified')}`
              : null,
            stats.unverified > 0
              ? `${stats.unverified} ${t('vc.imeiReport.coverage.unverified')}`
              : null,
            stats.notApplicable > 0
              ? `${stats.notApplicable} ${t('vc.imeiReport.coverage.na')}`
              : null,
            stats.warning > 0 ? `${stats.warning} ${t('vc.imeiReport.coverage.warning')}` : null,
            stats.fail > 0 ? `${stats.fail} ${t('vc.imeiReport.coverage.fail')}` : null,
          ]
            .filter(Boolean)
            .join(' · ')}
        </p>
      </div>
    </div>
  )
}

function ChecksCoverageSummary({ stats }: { stats: VerifyCoverageStats }) {
  const { t } = useTranslation()
  const parts: string[] = []
  if (stats.verified > 0) {
    parts.push(t('vc.imeiReport.checksSummary.verified', { count: stats.verified }))
  }
  if (stats.unverified > 0) {
    parts.push(t('vc.imeiReport.checksSummary.unverified', { count: stats.unverified }))
  }
  if (stats.notApplicable > 0) {
    parts.push(t('vc.imeiReport.checksSummary.na', { count: stats.notApplicable }))
  }
  if (!parts.length) return null
  return (
    <div className="vr-checks__summary" aria-label={parts.join(', ')}>
      {stats.verified > 0 ? (
        <span className="vr-checks__summary-item vr-checks__summary-item--verified">
          <span className="vr-checks__summary-dot vr-checks__summary-dot--verified" aria-hidden="true" />
          {t('vc.imeiReport.checksSummary.verified', { count: stats.verified })}
        </span>
      ) : null}
      {stats.unverified > 0 ? (
        <span className="vr-checks__summary-item vr-checks__summary-item--unverified">
          <span className="vr-checks__summary-dot vr-checks__summary-dot--unverified" aria-hidden="true" />
          {t('vc.imeiReport.checksSummary.unverified', { count: stats.unverified })}
        </span>
      ) : null}
      {stats.notApplicable > 0 ? (
        <span className="vr-checks__summary-item vr-checks__summary-item--na">
          <span className="vr-checks__summary-dot vr-checks__summary-dot--na" aria-hidden="true" />
          {t('vc.imeiReport.checksSummary.na', { count: stats.notApplicable })}
        </span>
      ) : null}
    </div>
  )
}

const VerifyPremiumReport = forwardRef<HTMLElement, Props>(function VerifyPremiumReport(
  {
    report,
    service,
    serviceTitle,
    variant,
    onClose,
    onDownloadPdf,
    pdfBusy,
    pdfError = '',
    onNewCheck,
    onRequireLogin,
    showDownloadPdf,
    guestLocked,
    preview = false,
    exportMode = false,
    onFollowUpCheck,
    followUpBusy = false,
    followUpError = '',
    followUpImeiPrompt = null,
    followUpPriceCents = {},
  },
  ref,
) {
  const { t, i18n } = useTranslation()
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const metaLine = useMemo(() => {
    const parts = [report.checkedAtLabel]
    if (report.luhnVerified) parts.push(t('vc.imeiReport.luhnVerified'))
    return parts.filter(Boolean).join(' · ')
  }, [report.checkedAtLabel, report.luhnVerified, t])

  const hubHeroSrc = imeiHubHeroImage(service)
  const hubHeroEmbed = Boolean(preview)
  const showHeroDeviceTitle = !hubHeroEmbed
  const showHeroVerdictBadge = !hubHeroEmbed

  const verdictKey = VERDICT_LABEL[report.verdict] ?? VERDICT_LABEL.unknown

  const trustPills = [
    report.showTrustIdentity ? t('vc.imeiReport.trust.identity') : null,
    report.showTrustTheft ? t('vc.imeiReport.trust.theft') : null,
    report.showTrustRealtime ? t('vc.imeiReport.trust.realtime') : null,
  ].filter(Boolean) as string[]

  return (
    <article
      ref={ref}
      className={`vr-report vr-report--${variant} vr-report--${report.verdict}${guestLocked ? ' vr-report--guest-locked' : ''}${preview ? ' vr-report--preview vr-report--hub-embed' : ''}${exportMode ? ' vr-report--export-pdf' : ''}`}
      aria-live={preview ? 'off' : 'polite'}
    >
      <header className="vr-report__top">
        <div className="vr-report__label-row">
          <span className="vr-report__dot" aria-hidden="true" />
          <span className="vr-report__label">{t('vc.imeiReport.label')}</span>
        </div>
        <div className="vr-report__top-actions">
          <span className="vr-report__service-pill">
            <span className="vr-report__service-icon" aria-hidden="true">
              <ImeiHubServicePhosphorIcon service={service} size={18} weight="duotone" />
            </span>
            <ImeiHubServiceTitle service={service} title={serviceTitle} variant="report" />
          </span>
          {onClose ? (
            <button type="button" className="vr-report__close" onClick={onClose} aria-label={t('vc.imeiReport.modalClose')}>
              <IconClose />
            </button>
          ) : null}
        </div>
      </header>

      <div className="vr-report__body">
        <div className="vr-hero-stage vr-hero-stage--enter">
          <section
            className="vr-hero vr-hero--hub-art"
            aria-labelledby={showHeroDeviceTitle ? 'vr-device-title' : undefined}
            aria-label={showHeroDeviceTitle ? undefined : report.deviceTitle}
          >
            <div className="vr-hero__copy">
              {showHeroDeviceTitle ? (
                <h2 id="vr-device-title" className="vr-hero__title">
                  {report.deviceTitle}
                </h2>
              ) : null}
              <div className="vr-hero__imei-row">
                <span className="vr-hero__imei">{report.imeiMasked}</span>
              </div>
              {metaLine ? <p className="vr-hero__meta">{metaLine}</p> : null}
            </div>

            <div className="vr-hero__scene vr-hero__scene--hub-art" aria-hidden="true">
              {hubHeroSrc ? (
                <img
                  src={hubHeroSrc}
                  alt=""
                  className="vr-hero__hub-art"
                  decoding="async"
                  draggable={false}
                />
              ) : (
                <>
                  <img src="/logo-mark.png?v=4" alt="" className="vr-hero__watermark" />
                  <span className="vr-hero__device-aura" />
                  <img
                    src="/images/verify-report-phone.png"
                    alt=""
                    className="vr-hero__phone"
                    decoding="async"
                    draggable={false}
                  />
                </>
              )}
              {!hubHeroSrc ? (
                <span className={`vr-hero__verdict vr-hero__verdict--${report.verdict}`}>
                  {report.verdict === 'clean' ? (
                    <svg className="vr-hero__verdict-icon" width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
                      <path
                        d="M20 6 9 17l-5-5"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ) : null}
                  {t(verdictKey)}
                </span>
              ) : null}
            </div>
          </section>

          {report.verdict === 'balance' ? (
            <div className="vr-balance-banner vr-balance-banner--overlap" role="alert">
              <p className="vr-balance-banner__title">{t('vc.imeiReport.balance.title')}</p>
              <p>{t('vc.imeiReport.balance.copy')}</p>
              {onNewCheck ? (
                <button type="button" className="vr-balance-banner__cta" onClick={onNewCheck}>
                  {t('vc.imeiReport.balance.cta')}
                </button>
              ) : null}
            </div>
          ) : (
            <section className="vr-verdict-card vr-verdict-card--overlap">
              <VerifyResultBadge
                stats={report.coverageStats}
                verdict={report.verdict}
                headlineKey={report.resultHeadlineKey}
              />
              <div className="vr-verdict-card__main">
                <p className="vr-verdict-card__summary">{report.summary}</p>
                {(exportMode || !preview) && trustPills.length > 0 ? (
                  <ul className="vr-trust-pills">
                    {trustPills.map((label) => (
                      <li key={label}>
                        <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
                          <path
                            d="M20 6 9 17l-5-5"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        {label}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            </section>
          )}
        </div>

        {report.criticalFindingKeys.length > 0 && report.verdict === 'risk' ? (
          <aside className="vr-risk-banner" role="alert">
            <p className="vr-risk-banner__title">{t('vc.imeiReport.criticalTitle')}</p>
            <ul>
              {report.criticalFindingKeys.map((key) => (
                <li key={key}>{t(key)}</li>
              ))}
            </ul>
          </aside>
        ) : null}

        {followUpImeiPrompt ? (
          <form className="vr-follow-up-imei" onSubmit={followUpImeiPrompt.onSubmit}>
            <p className="vr-follow-up-imei__title">{t('vc.imeiReport.followUp.enterImeiTitle')}</p>
            <p className="vr-follow-up-imei__lead">
              {t('vc.imeiReport.followUp.enterImeiLead', {
                masked: followUpImeiPrompt.masked,
                service: followUpImeiPrompt.serviceTitle,
              })}
            </p>
            <label className="vr-follow-up-imei__field">
              <span className="sr-only">{t('vc.imeiReport.followUp.enterImeiLabel')}</span>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="off"
                autoFocus
                className="vr-follow-up-imei__input"
                placeholder={t('vc.imeiReport.followUp.enterImeiPlaceholder')}
                value={followUpImeiPrompt.value}
                onChange={(event) => followUpImeiPrompt.onChange(event.target.value)}
                disabled={followUpImeiPrompt.busy}
              />
            </label>
            {followUpImeiPrompt.error ? (
              <p className="vr-follow-up-imei__error" role="alert">
                {followUpImeiPrompt.error}
              </p>
            ) : null}
            <div className="vr-follow-up-imei__actions">
              <button
                type="button"
                className="vr-follow-up-imei__cancel"
                onClick={followUpImeiPrompt.onCancel}
                disabled={followUpImeiPrompt.busy}
              >
                {t('common.cancel')}
              </button>
              <button type="submit" className="vr-follow-up-imei__submit" disabled={followUpImeiPrompt.busy}>
                {t('vc.imeiReport.followUp.enterImeiSubmit')}
              </button>
            </div>
          </form>
        ) : null}

        {followUpError ? (
          <p className="vr-follow-up-error" role="alert">
            {followUpError}
          </p>
        ) : null}

        {report.verdict !== 'invalid' && report.checks.length > 0 ? (
          <section className={`vr-checks${followUpBusy ? ' vr-checks--busy' : ''}`} aria-busy={followUpBusy}>
            {followUpBusy ? (
              <p className="vr-follow-up-busy" role="status">
                {t('vc.imeiReport.followUp.busy')}
              </p>
            ) : null}
            <div className="vr-checks__head">
              <div>
                <h3>{t('vc.imeiReport.checksHeading')}</h3>
                <p>{t('vc.imeiReport.checksLead')}</p>
              </div>
              <ChecksCoverageSummary stats={report.coverageStats} />
            </div>
            <div className="vr-checks__grid">
              {report.checks.map((check) => {
                const iconService = hubServiceForCheckIcon(check.id)
                const isFollowUpRow = Boolean(
                  !exportMode &&
                    check.status === 'unverified' &&
                    check.followUpService &&
                    onFollowUpCheck,
                )
                const canFollowUpAction = Boolean(isFollowUpRow && onFollowUpCheck && !exportMode)
                const followUpServiceTitle = check.followUpService
                  ? t(`vc.imeiHub.services.${check.followUpService}.title`)
                  : ''
                const followUpCents =
                  check.followUpService != null
                    ? followUpPriceCents[check.followUpService]
                    : undefined
                const followUpPriceLabel =
                  typeof followUpCents === 'number'
                    ? formatImeiHubPriceEur(followUpCents, i18n.language)
                    : null
                const expandable =
                  !isFollowUpRow && Boolean(check.detail && check.detail !== check.summary)
                const open = !exportMode && expandedId === check.id
                return (
                  <div
                    key={check.id}
                    className={`vr-check-row vr-check-row--${check.status}${open ? ' vr-check-row--open' : ''}${isFollowUpRow ? ' vr-check-row--follow-up' : ''}`}
                  >
                    <span className="vr-check-row__icon-wrap" aria-hidden="true">
                      <ImeiHubServicePhosphorIcon
                        service={iconService}
                        className="vr-check-row__icon"
                        size={20}
                        weight="regular"
                      />
                    </span>
                    <button
                      type="button"
                      className="vr-check-row__main"
                      disabled={exportMode || (!canFollowUpAction && !expandable)}
                      aria-expanded={expandable ? open : undefined}
                      aria-label={
                        canFollowUpAction
                          ? t('vc.imeiReport.followUp.aria', { service: followUpServiceTitle })
                          : undefined
                      }
                      onClick={() => {
                        if (canFollowUpAction && check.followUpService) {
                          onFollowUpCheck?.(check.followUpService, check.id)
                          return
                        }
                        if (expandable) setExpandedId(open ? null : check.id)
                      }}
                    >
                      <span className="vr-check-row__text">
                        <span className="vr-check-row__label">{t(check.labelKey)}</span>
                        {check.summary ? <span className="vr-check-row__sub">{check.summary}</span> : null}
                        {isFollowUpRow ? (
                          <span className="vr-check-row__follow-hint">
                            {followUpPriceLabel
                              ? t('vc.imeiReport.followUp.hintPaid', {
                                  service: followUpServiceTitle,
                                  price: followUpPriceLabel,
                                })
                              : t('vc.imeiReport.followUp.hint', { service: followUpServiceTitle })}
                          </span>
                        ) : null}
                      </span>
                      {isFollowUpRow && followUpPriceLabel ? (
                        <span className="vr-check-row__follow-price">{followUpPriceLabel}</span>
                      ) : null}
                      <VerifyStatusBadge status={check.status} interactive={isFollowUpRow} />
                      {canFollowUpAction || expandable ? (
                        <span className="vr-check-row__chev" aria-hidden="true" />
                      ) : null}
                    </button>
                    {open && check.detail ? <p className="vr-check-row__detail">{check.detail}</p> : null}
                  </div>
                )
              })}
            </div>
          </section>
        ) : report.verdict === 'invalid' ? (
          <p className="vr-invalid">{report.summary}</p>
        ) : null}
      </div>

      {preview ? null : exportMode ? (
        <footer className="vr-footer vr-footer--export-pdf">
          <div className="vr-footer__secure">
            <IconLock className="vr-footer__secure-icon" />
            <div>
              <strong>{t('vc.imeiHub.security')}</strong>
              <p>{t('vc.imeiHub.privacy')}</p>
            </div>
          </div>
        </footer>
      ) : (
        <footer className="vr-footer">
          <div className="vr-footer__secure">
            <IconLock className="vr-footer__secure-icon" />
            <div>
              <strong>{t('vc.imeiHub.security')}</strong>
              <p>{t('vc.imeiHub.privacy')}</p>
            </div>
          </div>
          <div className="vr-footer__actions">
            {report.savedToProfile ? (
              <span className="vr-footer__saved">
                <IconBookmark />
                {t('vc.imeiReport.saved')}
              </span>
            ) : onRequireLogin ? (
              <button type="button" className="vr-btn vr-btn--ghost" onClick={onRequireLogin}>
                {t('vc.imeiReport.saveToProfile')}
              </button>
            ) : null}
            {onNewCheck ? (
              <button type="button" className="vr-btn vr-btn--primary" onClick={onNewCheck}>
                <IconRefresh />
                {t('vc.imeiReport.newCheck')}
              </button>
            ) : null}
            {showDownloadPdf && onDownloadPdf ? (
              <button type="button" className="vr-btn vr-btn--outline" disabled={pdfBusy} onClick={onDownloadPdf}>
                {pdfBusy ? t('vc.imeiReport.downloadPdfBusy') : t('vc.imeiReport.downloadPdf')}
              </button>
            ) : null}
            {pdfError ? (
              <p className="vr-footer__pdf-error" role="alert">
                {pdfError}
              </p>
            ) : null}
          </div>
        </footer>
      )}

      {guestLocked ? (
        <div className="vc-imei-report__guest-overlay" role="region" aria-label={t('vc.imeiReport.guestUnlockHint')}>
          <p className="vc-imei-report__guest-overlay-copy">
            {t('vc.imeiReport.guestUnlockHint')}{' '}
            <button type="button" className="vc-imei-report__guest-overlay-cta" onClick={onRequireLogin}>
              {t('vc.imeiHub.reportsRegister')}
            </button>
          </p>
        </div>
      ) : null}
    </article>
  )
})

export default VerifyPremiumReport
